import { FC, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";

import Button from "../../components/Button/Button";
import { selectAuthLoadingUpdate, selectAuthUser } from "../../store/selectors/auth";
import FormikInputField from "../../components/FormikField/FormikInputField/FormikInputField";
import FormikSelectField from "../../components/FormikField/FormikSelectField/FormikSelectField";

import SettingLayout from "../../layout/SettingLayout/SettingLayout";
import Avatar from "../../components/Avatar/Avatar";
import BrandLogo from "../../components/BrandLogo/BrandLogo";
import { selectBrandState } from "../../store/selectors/brands";
import { updateUser } from "../../store/auth/slice";
import ModalResetPassword from "../../components/ModalResetPassword/ModalResetPassword";
import { updateBrand } from "../../store/brands/slice";
import { selectSuperuserSupermode } from "../../store/selectors/superuser";
import { profileSchema } from "../../utils/validators";
import AgencyModeCard from "./AgencyModeCard";
import { useBrandMetadata } from "../../hooks/useBrandMetadata";

import styles from "./Settings.module.css";


const WordCounter: FC<{ name: string; minWords: number }> = ({ name, minWords }) => {
  const { values } = useFormikContext<Record<string, string>>();
  const text = values[name] || "";
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const remaining = Math.max(0, minWords - wordCount);

  return (
    <span style={{ fontWeight: 'normal' }}>
      {remaining > 0 ? `${remaining} words needed` : `${wordCount} words`}
    </span>
  );
};


const SettingsView: FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const brand = useSelector(selectBrandState);
  const loadingUpdate = useSelector(selectAuthLoadingUpdate);
  const isSupermode = useSelector(selectSuperuserSupermode);
  const { brandTypes, industries, primaryLanguages } = useBrandMetadata();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!user) return null;

  const initialValues = {
    firstName: user.user_metadata.first_name || user.user_metadata.full_name?.split(' ')[0] || '',
    lastName: user.user_metadata.last_name || user.user_metadata.full_name?.split(' ')[1] || '',
    email: user.email || '',
    companyName: brand?.name || '',
    brandDescription: brand?.description || '',
    brandWebsite: brand?.website || '',
    brandRegion: brand?.region || '',
    brandType: brand?.brandType || '',
    brandIndustry: brand?.industry || '',
    brandLanguage: brand?.primaryLanguage || 'English',
    brandAudience: brand?.targetAudience || '',
  };

  const handleResetPassword = () => {
    setIsModalOpen(true);
  };

  const handleUpdateUser = (values: typeof initialValues) => {
    dispatch(updateUser(values));
  };

  const handleUpdateBrand = (values: typeof initialValues) => {
    dispatch(updateBrand({
      name: values.companyName,
      description: values.brandDescription,
      website: values.brandWebsite,
      region: values.brandRegion || undefined,
      brandType: values.brandType || undefined,
      industry: values.brandIndustry || undefined,
      primaryLanguage: values.brandLanguage || undefined,
      targetAudience: values.brandAudience || undefined,
    }));
  };

  const handleSubmit = (values: typeof initialValues) => {
    const changedValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (value !== initialValues[key as keyof typeof initialValues]) {
        acc[key as keyof typeof initialValues] = value;
      }
      return acc;
    }, {} as Partial<typeof initialValues>);

    const hasBrandChanges = changedValues.companyName
      || changedValues.brandDescription !== undefined
      || changedValues.brandWebsite !== undefined
      || changedValues.brandRegion !== undefined
      || changedValues.brandType !== undefined
      || changedValues.brandIndustry !== undefined
      || changedValues.brandLanguage !== undefined
      || changedValues.brandAudience !== undefined;

    const updateActions = [
      {
        condition: changedValues.firstName || changedValues.lastName || changedValues.email,
        action: () => handleUpdateUser(values)
      },
      {
        condition: hasBrandChanges,
        action: () => handleUpdateBrand(values)
      }
    ];

    updateActions
      .filter(({ condition }) => condition)
      .forEach(({ action }) => action());
  };

  return (
    <SettingLayout>
      <div className={styles.settings}>
        <Avatar className={styles.settings__avatar} size="lg" />

        <Formik
          initialValues={initialValues}
          validationSchema={profileSchema}
          validationContext={{ isSupermode }}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form className={styles.form}>
              <div className={styles.form__field}>
                <FormikInputField label="Email" name="email" placeholder="Email" type="email" disabled />
              </div>

              {!isSupermode && (
                <>
                  <div className={styles.form__field}>
                    <FormikInputField label="First Name" name="firstName" placeholder="First Name" type="text" />
                  </div>

                  <div className={styles.form__field}>
                    <FormikInputField label="Last Name" name="lastName" placeholder="Last Name" type="text" />
                  </div>
                </>
              )}

              <div className={styles.form__field}>
                <FormikInputField label="Company Name" name="companyName" placeholder="Company Name" type="text" />
              </div>

              {!isSupermode && (
                <button className={styles.form__resetpass} type="button" onClick={handleResetPassword}>Change password</button>
              )}

              <div className={styles.form__divider}>
                <hr />
                <span>Brand Profile</span>
                <hr />
              </div>

              {brand?.logoUrl && (
                <div className={styles.form__brand_logo}>
                  <BrandLogo logoUrl={brand.logoUrl} name={brand.name} size="large" />
                </div>
              )}

              <div className={styles.form__field}>
                <FormikInputField label="Website" name="brandWebsite" placeholder="e.g. example.com" type="text" />
              </div>

              <div className={styles.form__field}>
                <FormikInputField label="Region of Presence" name="brandRegion" placeholder="e.g. Global, US, Europe" type="text" />
              </div>

              <div className={styles.form__row}>
                <div className={styles.form__field}>
                  <FormikSelectField label="Brand Type" name="brandType" options={brandTypes} placeholder="Select type" />
                </div>

                <div className={styles.form__field}>
                  <FormikSelectField label="Industry" name="brandIndustry" options={industries} placeholder="Select industry" />
                </div>
              </div>

              <div className={styles.form__row}>
                <div className={styles.form__field}>
                  <FormikSelectField label="Primary Language" name="brandLanguage" options={primaryLanguages} />
                </div>
              </div>

              <div className={styles.form__field}>
                <FormikInputField
                  multiline
                  label="Brand Description"
                  rightLabel={<WordCounter name="brandDescription" minWords={50} />}
                  name="brandDescription"
                  placeholder={"Describe what your brand is and what makes it unique. Include:\n• Key features and benefits that set you apart\n• What problems you solve\n• Quality, pricing, or service differentiators\n• Specific results or outcomes you deliver\n• Why customers should choose you over alternatives"}
                  rows={6}
                />
              </div>

              <div className={styles.form__field}>
                <FormikInputField
                  multiline
                  label="Target Audience"
                  rightLabel={<WordCounter name="brandAudience" minWords={15} />}
                  name="brandAudience"
                  placeholder={"Describe your ideal customers:\n• Interests and behaviors in general\n• Pain points and needs they struggle with\n• Where they spend time online/offline"}
                  rows={4}
                />
              </div>

              <Button label="Save changes" typeStyle="fill-dark" size="lg" className={styles.form__submit} type="submit" disabled={loadingUpdate} />
            </Form>
          )}
        </Formik>

        <ModalResetPassword isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

        {!isSupermode && <AgencyModeCard />}
      </div>
    </SettingLayout>
  );
};

export default SettingsView;
